import * as d3 from 'd3';

import { IAxedSeries } from './IAxedSeries';
import { IColorHelper, ColorHelper, ILogger, LoggerFactory } from '../../../helpers/helpers';

export class AreaSeries implements IAxedSeries {
    y0: { (d: any, i?: number): number };
    y1: { (d: any, i?: number): number };
    name: string;

    private _area: d3.svg.Area<any>;
    private _color: string;
    private _colorHelper: IColorHelper;
    private _crosshairFormat: any;
    private _data;
    private _group: d3.Selection<any>;
    private _identifier: any;
    private _isLineVisible: boolean = true;
    private _isVisible: boolean;
    private _legendText: string;
    private _logger: ILogger;
    private _selectionFunction: any;
    private _transparency = 0.3;
    private _valueFormatter = '.2f';
    private _xScale: d3.time.Scale<any, any>;
    private _yScale;

    constructor(
        container: d3.Selection<any>,
        name: string,
        color: string,
        y0: { (data: any, index?: number): any },
        y1: { (data: any, index?: number): any },
        identifier, selector
    ) {
        this.y0 = y0;
        this.y1 = y1;
        this.name = name;
        this._legendText = this.name;
        this._colorHelper = new ColorHelper();
        this.color(color);
        this.identifier(identifier);
        this.selector(selector);
        this.init(container, color);
    }

    private init(container: d3.Selection<any>, color: string) {
        this._logger = LoggerFactory.getLogger('WideTech.Charting.areaSeries');
        this._group = container
            .append('g')
            .classed('series', true);
        const rgba = this._colorHelper.hexToRgb(color);
        rgba.a = this._transparency;
        this._group.append('path')
            .classed('area', true)
            .classed('trend', true)
            .style({
                stroke: this._isLineVisible ? color : null,
                fill: rgba.css()
            });

        this.isVisible(true);
    }

    update(data: any, xScale, yScale): void {
        this.data(data);
        this._xScale = xScale;
        this._yScale = yScale;
        const self = this;
        if (data) {
            let currentData = [];
            if (this.selector()) {
                currentData = data.filter(d => {
                    let result = false;
                    if (self.selector()(d) === self.identifier()) {
                        const position = self._xScale(new Date(d.timeStamp));
                        if (position >= 0 && position <= self._xScale.range()[1]) {
                            // if (self.y0(d) !== null && self.y1(d) !== null) {
                            result = true;
                            // }
                        }
                    }
                    return result;
                });
            } else {
                currentData = data;
            }
            this.draw(currentData);
        }
    }

    private draw(data: any[]) {
        const selection = this._group.select('path');
        try {
            this._area = d3.svg.area()
                .x((d: any, i) => this._xScale(new Date(d.timeStamp)))
                .y0((d, i) => this._yScale(this.y0(d, i)))
                .y1((d, i) => this._yScale(this.y1(d, i)))
                .defined((d, i) => {
                    const y0 = this.y0(d, i);
                    const y1 = this.y1(d, i);
                    return y0 !== null && y0 !== undefined
                        && y1 !== null && y1 !== undefined;
                });
            const path = this._area(data);
            selection
                .attr('d', path);
        } catch (e) {
            this._logger.error(e);
        }
    }

    translate(translateX: number, translateY: number) {
        //
    }

    color(value?: string): string {
        if (value) {
            this._color = value;
            //TODO update chart if color changes
        }
        return this._color;
    }

    legendText(text?: string): string {
        if (text) {
            this._legendText = text;
        }
        return this._legendText;
    }

    data(value?: any[]): any[] {
        if (value) {
            const self = this;
            let currentData = [];
            if (this.selector()) {
                currentData = value.filter(d => self.selector()(d) === self.identifier());
            } else {
                currentData = value;
            }
            this._data = currentData;
        }
        return this._data;
    }

    changeXScale(xScale) {
        this.updateScales(xScale, this._yScale);
    }

    updateScales(xScale, yScale) {
        this.changeScale(xScale, yScale);
    }

    changeScale(xScale, yScale) {
        this.update(this._data, xScale, yScale);
    }

    selector(selectionFunction?): { (d): any } {
        if (selectionFunction) {
            this._selectionFunction = selectionFunction;
        }
        return this._selectionFunction;
    }

    identifier(value?: any): any {
        if (value !== null && value !== undefined) {
            this._identifier = value;
        }
        return this._identifier;
    }

    min(): number {
        const self = this;
        let min = null;
        if (self._data) {
            const minY0 = d3.min(self._data, d => self.y0(d));
            const minY1 = d3.min(self._data, d => self.y1(d));
            min = Math.min(minY0, minY1);
        }
        return min;
    }

    max(): number {
        const self = this;
        let max = null;
        if (self._data) {
            const maxY0 = d3.max(self._data, d => self.y0(d));
            const maxY1 = d3.max(self._data, d => self.y1(d));
            max = Math.max(maxY0, maxY1);
        }
        return max;
    }

    remove(): void {
        this._group.remove();
    }

    crosshairLabel(bisect, date, valueFormatter): string {
        const currentValue = this.data()[bisect(this.data(), date)];
        let label = '';
        if (currentValue) {
            //Temporary fix; should be made customizable
            //label = this.legendText() + ': ' + valueFormatter(this.y0(currentValue)) + ' - ' + valueFormatter(this.y1(currentValue));
            if (!this._crosshairFormat) {
                const y = this.y1(currentValue);
                label = this.legendText() + ': ' + ((y === null || y === undefined) ? y : valueFormatter(y));
            } else {
                label = this._crosshairFormat(currentValue);
            }
        }
        return label;
    }

    transparency(value?: number): number {
        if (value) {
            this._transparency = value;
            const rgba = this._colorHelper.hexToRgb(this._color);
            rgba.a = this._transparency;
            this._group.selectAll('.area').style('fill', rgba.css());
        }
        return this._transparency;
    }

    valueFormatter(value?: string): string {
        if (value) {
            this._valueFormatter = value;
        }
        return this._valueFormatter;
    }

    resize(xScale, yScale) {
        if (this._xScale) {
            this._xScale.range(xScale.range());
        } else {
            this._xScale = xScale;
        }
        if (this._yScale) {
            this._yScale.range(yScale.range());
        } else {
            this._yScale = yScale;
        }
        this.changeScale(this._xScale, this._yScale);
    }

    isVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isVisible = value;
            if (this._isVisible) {
                this._group.style('visibility', 'visible');
            } else {
                this._group.style('visibility', 'hidden');
            }
        }
        return this._isVisible;
    }

    crosshairFormat(): (d: any, i?) => string;
    crosshairFormat(value: (d: any, i?) => string): this;
    crosshairFormat(value?: (d: any, i?) => string): this | ((d: any, i?: number) => string) {
        if (arguments.length) {
            this._crosshairFormat = value;
            return this;
        }
        return this._crosshairFormat;
    }

    isLineVisible(): boolean;
    isLineVisible(value: boolean): this;
    isLineVisible(value?: boolean): this | boolean {
        if (arguments.length) {
            this._isLineVisible = value;
            this._group.select('path')
                .style('stroke', this._isLineVisible ? this._color : null);

            return this;
        }
        return this._isLineVisible;
    }

    setSerieState(clicked: boolean, state?: number, reset?: boolean) {
        //
    }

    isStacked() {
        return false;
    }

    isXAxis() {
        return false;
    }
}